"use strict";

exports.__esModule = true;
exports.Slot = void 0;

var _Attachment = require("./attachments/Attachment");

var _Utils = require("./Utils");

/******************************************************************************
 * Spine Runtimes License Agreement
 * Last updated January 1, 2020. Replaces all prior versions.
 *
 * Copyright (c) 2013-2020, Esoteric Software LLC
 *
 * Integration of the Spine Runtimes into software or otherwise creating
 * derivative works of the Spine Runtimes is permitted under the terms and
 * conditions of Section 2 of the Spine Editor License Agreement:
 * http://esotericsoftware.com/spine-editor-license
 *
 * Otherwise, it is permitted to integrate the Spine Runtimes into software
 * or otherwise create derivative works of the Spine Runtimes (collectively,
 * "Products"), provided that each user of the Products must obtain their own
 * Spine Editor license and redistribution of the Products in any form must
 * include this license and copyright notice.
 *
 * THE SPINE RUNTIMES ARE PROVIDED BY ESOTERIC SOFTWARE LLC "AS IS" AND ANY
 * EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED
 * WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE
 * DISCLAIMED. IN NO EVENT SHALL ESOTERIC SOFTWARE LLC BE LIABLE FOR ANY
 * DIRECT, INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES
 * (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES,
 * BUSINESS INTERRUPTION, OR LOSS OF USE, DATA, OR PROFITS) HOWEVER CAUSED AND
 * ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT
 * (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE OF
 * THE SPINE RUNTIMES, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
 *****************************************************************************/

/** Stores a slot's current pose. Slots organize attachments for {@link Skeleton#drawOrder} purposes and provide a place to store
 * state for an attachment. State cannot be stored in an attachment itself because attachments are stateless and may be shared
 * across multiple skeletons. */
var Slot = /*#__PURE__*/function () {
  function Slot(data, bone) {
    /** The slot's setup pose data. */
    this.data = null;
    /** The bone this slot belongs to. */

    this.bone = null;
    /** The color used to tint the slot's attachment. If {@link #getDarkColor()} is set, this is used as the light color for two
     * color tinting. */

    this.color = null;
    /** The dark color used to tint the slot's attachment for two color tinting, or null if two color tinting is not used. The dark
     * color's alpha is not used. */

    this.darkColor = null;
    this.attachment = null;
    this.attachmentTime = 0;
    this.attachmentState = 0;
    /** Values to deform the slot's attachment. For an unweighted mesh, the entries are local positions for each vertex. For a
     * weighted mesh, the entries are an offset for each vertex which will be added to the mesh's local vertex positions.
     *
     * See {@link VertexAttachment#computeWorldVertices()} and {@link DeformTimeline}. */

    this.deform = new Array();
    if (!data) throw new Error("data cannot be null.");
    if (!bone) throw new Error("bone cannot be null.");
    this.data = data;
    this.bone = bone;
    this.color = new _Utils.Color();
    this.darkColor = !data.darkColor ? null : new _Utils.Color();
    this.setToSetupPose();
  }
  /** The skeleton this slot belongs to. */


  var _proto = Slot.prototype;

  _proto.getSkeleton = function getSkeleton() {
    return this.bone.skeleton;
  }
  /** The current attachment for the slot, or null if the slot has no attachment. */
  ;

  _proto.getAttachment = function getAttachment() {
    return this.attachment;
  }
  /** Sets the slot's attachment and, if the attachment changed, resets {@link #attachmentTime} and clears the {@link #deform}.
   * The deform is not cleared if the old attachment has the same {@link VertexAttachment#getDeformAttachment()} as the specified
   * attachment.
   * @param attachment May be null. */
  ;

  _proto.setAttachment = function setAttachment(attachment) {
    if (this.attachment == attachment) return;

    if (!(attachment instanceof _Attachment.VertexAttachment) || !(this.attachment instanceof _Attachment.VertexAttachment) || attachment.deformAttachment != this.attachment.deformAttachment) {
      this.deform.length = 0;
    }

    this.attachment = attachment;
    this.attachmentTime = this.bone.skeleton.time;
  };

  _proto.setAttachmentTime = function setAttachmentTime(time) {
    this.attachmentTime = this.bone.skeleton.time - time;
  }
  /** The time that has elapsed since the last time the attachment was set or cleared. Relies on Skeleton
   * {@link Skeleton#time}. */
  ;

  _proto.getAttachmentTime = function getAttachmentTime() {
    return this.bone.skeleton.time - this.attachmentTime;
  }
  /** Sets this slot to the setup pose. */
  ;

  _proto.setToSetupPose = function setToSetupPose() {
    this.color.setFromColor(this.data.color);
    if (this.darkColor) this.darkColor.setFromColor(this.data.darkColor);
    if (!this.data.attachmentName) this.attachment = null;else {
      this.attachment = null;
      this.setAttachment(this.bone.skeleton.getAttachment(this.data.index, this.data.attachmentName));
    }
  };

  return Slot;
}();

exports.Slot = Slot;
//# sourceMappingURL=data:application/json;charset=utf-8;base64,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